import { getBackendUrl } from './url'

// Base URL for backend requests from environment variable
const BACKEND_BASE_URL = getBackendUrl()

export interface MapDetails {
	name: string
	label: string
	altNames?: string[]
	iconUrl: string
	neutralSheepCount: number
}

export interface CivDetails {
	name: string
	label: string
	draftFlagUrl: string
	originalFlagUrl: string
	aoe2cm: string
}

export let maps: MapDetails[] = []

// Try Catch blocks generated with GPT 5.0
// Load maps from backend config route and cache in-module
try {
	fetch(`${BACKEND_BASE_URL}/api/config/maps`)
		.then((res) =>
			res.ok
				? res.json()
				: Promise.reject(
						new Error(`Failed to load maps: ${res.status}`),
					),
		)
		.then((data: MapDetails[]) => {
			if (Array.isArray(data)) {
				maps = data
			}
		})
		.catch(() => {
			// Silently ignore; fallback behavior in findMap will handle unknowns
		})
} catch {
	// Ignore environments without fetch
}

export let civs: CivDetails[] = []

// Load civs from backend config route and cache in-module
try {
	fetch(`${BACKEND_BASE_URL}/api/config/civs`)
		.then((res) =>
			res.ok
				? res.json()
				: Promise.reject(
						new Error(`Failed to load civs: ${res.status}`),
					),
		)
		.then((data: any[]) => {
			if (Array.isArray(data)) {
				civs = data.map((civ: any) => ({
					name: civ.label,
					label: civ.label,
					draftFlagUrl: civ.draftFlag,
					originalFlagUrl: civ.originalFlag,
					aoe2cm: civ.aoe2cm,
				}))
			}
		})
		.catch(() => {
			// Silently ignore; fallback behavior in findCiv will handle unknowns
		})
} catch {
	// Ignore environments without fetch
}

const fallbackMap = {
	name: 'Unknown',
	label: '?',
	iconUrl: '/maps/megarandom.png',
	neutralSheepCount: 0,
}

interface FindMapOptions {
	withFallback?: boolean
}

export function findMap(
	map: string | null,
	options: FindMapOptions & { withFallback: true },
): MapDetails
export function findMap(
	map: string | null,
	options?: FindMapOptions,
): MapDetails | null

export function findMap(
	map: string | null,
	options?: { withFallback?: boolean },
) {
	const { withFallback } = options || {}
	if (!map) {
		return withFallback ? fallbackMap : null
	}

	const mapDetails = maps.find(
		(item) =>
			item.label === map ||
			item.name === map ||
			item?.altNames?.includes(map),
	)

	if (mapDetails) {
		return mapDetails
	} else if (withFallback) {
		return { ...fallbackMap, label: map }
	} else {
		return null
	}
}

// ----------------------

const fallbackCiv: CivDetails = {
	name: 'Unknown',
	label: '?',
	draftFlagUrl: '/draftFlags/Random.png',
	originalFlagUrl: '/civFlags/Random.png',
	aoe2cm: '',
}

interface FindCivOptions {
	withFallback?: boolean
}

export function findCiv(
	civ: string | null,
	options: FindCivOptions & { withFallback: true },
): CivDetails
export function findCiv(
	civ: string | null,
	options?: FindCivOptions,
): CivDetails | null

export function findCiv(
	civ: string | null,
	options?: { withFallback?: boolean },
) {
	const { withFallback } = options || {}

	if (!civ) {
		return withFallback ? fallbackCiv : null
	}

	const civDetails = civs.find(
		(item) =>
			item.label === civ || item.name === civ || item.aoe2cm === civ,
	)

	if (civDetails) {
		return civDetails
	} else if (withFallback) {
		return { ...fallbackCiv, label: civ }
	} else {
		return null
	}
}
